import { Dispatch, Reducer } from 'react';
import { action, on, props, reducer, union } from 'ts-action';

import { Account, Retailer } from '@packages/models/api';

export enum AuthStatus {
    Unknown = 'UNKNOWN',
    Checking = 'CHECKING_AUTH',
    Saving = 'SAVING_TOKEN',
    FetchingProfile = 'FETCHING_PROFILE',
    Authenticated = 'AUTHENTICATED',
    Unauthenticated = 'UNAUTHENTICATED',
}

/**
 * Represents the cause of an auth state change.
 * Used to differentiate between explicit sign-ins/sign-outs and silent restores.
 */
export enum AuthTransition {
    /** No specific cause; initial or unknown state */
    None = 'NONE',

    /** Silent restore from stored token on page reload */
    Restore = 'RESTORE',

    /** Fresh credential exchange (SAML callback with accessToken) */
    SignIn = 'SIGN_IN',

    /** Explicit sign-out (user click, idle timeout, 401 redirect) */
    SignOut = 'SIGN_OUT',
}

export interface AuthState {
    status: AuthStatus;
    token: string;
    account: Account | null;
    retailers: Retailer[];
    showUnauthorizedPrompt: boolean;
    authTransition: AuthTransition;
}

export const authActions = {
    resetState: action('RESET_AUTH_STATE'),
    setAuthStatus: action('SET_AUTH_STATUS', props<{ status: AuthState['status']; token?: string }>()),
    setAccount: action('SET_AUTH_ACCOUNT', props<{ account: AuthState['account'] }>()),
    setRetailers: action('SET_RETAILERS', props<{ retailers: AuthState['retailers'] }>()),
    updateToken: action('UPDATE_TOKEN', props<{ token: AuthState['token'] }>()),
    setShowUnauthorizedPrompt: action(
        'SET_SHOW_UNAUTHORIZED_PROMPT',
        props<{ showUnauthorizedPrompt: AuthState['showUnauthorizedPrompt'] }>()
    ),
    setAuthTransition: action('SET_AUTH_TRANSITION', props<{ authTransition: AuthState['authTransition'] }>()),
};

const actionsUnion = union(...Object.values(authActions));
type AuthActions = typeof actionsUnion.actions;

export type AuthActionDispatcher = Dispatch<AuthActions>;

export const initialState = {
    status: AuthStatus.Unknown,
    token: '',
    account: null,
    retailers: [],
    showUnauthorizedPrompt: false,
    authTransition: AuthTransition.None,
} as AuthState;

export const authReducer: Reducer<AuthState, AuthActions> = reducer(
    initialState,
    on(authActions.resetState, (state) => {
        return {
            ...state,
            ...initialState,
            status: AuthStatus.Unauthenticated,
            authTransition: AuthTransition.SignOut,
        };
    }),
    on(authActions.setAuthStatus, (state, { status, token }) => {
        const newState: AuthState = { ...state, status };
        if (typeof token !== 'undefined') {
            newState.token = token;
        }

        return newState;
    }),
    on(authActions.setAccount, (state, { account }) => {
        return {
            ...state,
            account,
        };
    }),
    on(authActions.setRetailers, (state, { retailers }) => {
        return {
            ...state,
            retailers,
        };
    }),
    on(authActions.updateToken, (state, { token }) => {
        return {
            ...state,
            token,
        };
    }),
    on(authActions.setShowUnauthorizedPrompt, (state, { showUnauthorizedPrompt }) => {
        return {
            ...state,
            showUnauthorizedPrompt,
        };
    }),
    on(authActions.setAuthTransition, (state, { authTransition }) => {
        return {
            ...state,
            authTransition,
        };
    })
);
